import axios from "api/axios";
import {
  deleteAppointmentChecklistEntry,
  patchAppointmentChecklistEntry,
  postAppointmentChecklistEntry,
} from "features/requests/api/appointmentChecklistEntries";
import { getAppointmentChecklists } from "features/requests/api/getAppointmentChecklists";
import type { IChecklistWithResponses } from "features/requests/types/checklists";
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import type { TChecklistPickerRow } from "components/checklist_dropdown";

// ============================================================================
// Types
// ============================================================================

export type TChecklistTemplateRow = {
  id: number;
  fk_appointment_type_id: number | null;
  fk_sub_appointment_type_id: number | null;
  label: string | null;
  order: number | null;
  required: boolean | null;
};

/**
 * Single row in the checklist panel. Combines existing entries (entry_id
 * != null) and would-be POSTs (entry_id == null) under one shape, plus the
 * `originalLabel`/`originalRequired` snapshot used by `flushPending` to
 * detect patches against the BE.
 */
export type ChecklistRow = {
  /** Stable React key. */
  key: string;
  /** Underlying eservbusiness.checklist.id (used for POST + lookups). */
  template_id: number;
  /** Saved entry id, or null if this row is a would-be POST. */
  entry_id: number | null;
  /** Label/required from the BE — used to detect patches against the original. */
  originalLabel: string;
  originalRequired: boolean | null;
  label: string;
  required: boolean | null;
  is_done: boolean;
  /** True if the user dismissed this row. Hidden from display, DELETEd on save. */
  dismissed: boolean;
};

export type UseChecklistRowsOptions = {
  appointmentIdNum: number | null;
  /** Normalized: `null` when the type is empty (Number("") === 0 footgun, audit 005 §F2). */
  actualTypeOrNull: number | null;
  defaultNum: number | null;
  /** "(No auto-fill)" with a sub-default picker means the user opted out — drop placeholder rows. */
  allowChecklistAutoOffer: boolean;
  /**
   * The appointment's saved (type, default) scope on the BE — derived directly
   * from the `appointment` prop in the parent. This is the source of truth for
   * "is the FE-current scope still the appointment's saved scope?" Pass `null`
   * for create-mode or when the appointment isn't loaded yet.
   *
   * Avoid using a ref-capture: capturing from the FE scope at first render
   * (or after a flush reset) drifts whenever the user picks a different
   * default before saving, which breaks the diverged-vs-saved branch.
   */
  savedScope: { type: number | null; default: number | null } | null;
};

export type ChecklistsApi = {
  rows: ChecklistRow[];
  /** Rows with `dismissed=false`. Use for both render and the panel count. */
  displayRows: ChecklistRow[];
  /** Templates in scope not yet on the appointment — drives the "(No auto-fill)" hint UI. */
  scopedTemplatesNotYetAdded: TChecklistTemplateRow[];
  isLoading: boolean;
  isError: boolean;
  templatesIsLoading: boolean;
  handleLabelChange: (row: ChecklistRow, nextLabel: string) => void;
  handleRequiredChange: (row: ChecklistRow, checked: boolean) => void;
  handleDismissRow: (row: ChecklistRow) => void;
  handleUndoPatchesForRow: (row: ChecklistRow) => void;
  handleConfirmTemplatesFromPicker: (picked: TChecklistPickerRow[]) => void;
  flushPending: (apptId: number) => Promise<void>;
};

// ============================================================================
// Helper: scope-match predicate
// ============================================================================

const templateMatchesAppointmentScope = (
  row: TChecklistTemplateRow,
  typeId: number,
  defaultId: number | null
) => {
  if (Number(row.fk_appointment_type_id) !== typeId) return false;
  if (defaultId == null) {
    return row.fk_sub_appointment_type_id == null;
  }
  return Number(row.fk_sub_appointment_type_id) === defaultId;
};

// ============================================================================
// Hook
// ============================================================================

/**
 * Owns the appointment-modal's checklist row state. Phase 2b lift of the
 * state machine that previously lived inside `AppointmentModalChecklistsSection`.
 *
 * Two queries:
 *   - `checklistQuery` — `GET /api/v1/company/appointment-checklists/:id`,
 *     returns rows scoped to the appointment's *saved* type/default.
 *   - `templatesQuery` — `GET /api/v1/eserv-business/checklist`, all company
 *     templates. Used for client-side filtering when the FE scope diverges
 *     from `savedScope`.
 *
 * Rebuild rules (audit 005 §10, audit 006 §14.Q1–Q4):
 *   - At saved scope: V2 already returns the right set. Use it directly,
 *     skipping placeholders when `allowChecklistAutoOffer` is false.
 *   - Diverged from saved scope: existing entries (entry_id != null) belong
 *     to the old scope and DELETE on save → mark dismissed. Then add
 *     new-scope templates as pending POSTs (when autofill is allowed).
 *
 * `flushPending` diffs `rows` vs the BE existing entries:
 *   - Existing entries absent or dismissed → DELETE.
 *   - Kept rows with edited label/required → PATCH.
 *   - Rows with no entry_id and not dismissed → POST.
 */
export function useChecklistRows(opts: UseChecklistRowsOptions): ChecklistsApi {
  const {
    appointmentIdNum,
    actualTypeOrNull,
    defaultNum,
    allowChecklistAutoOffer,
    savedScope,
  } = opts;

  const { t } = useTranslation();
  const tBase = "containers.appointmentCard.appointment-folder.appointmentModal";
  const queryClient = useQueryClient();

  const [rows, setRows] = useState<ChecklistRow[]>([]);

  const checklistQuery = useQuery(
    ["appointment-checklists", appointmentIdNum ?? "new"],
    () => getAppointmentChecklists(appointmentIdNum as number),
    { enabled: appointmentIdNum != null, refetchOnWindowFocus: false }
  );

  // Single cache slot per company. The `/eserv-business/checklist` endpoint
  // returns ALL templates regardless of params, so keying by (type, default)
  // would force a refetch every time the FE scope changed — and during that
  // refetch the build effect's `templatesQuery.isLoading` gate would early-
  // return, leaving `rows` stale. (Observed regression on create flow:
  // picking a sub-default invalidated the cache, the rebuild stalled, and a
  // quick Submit POSTed the prior parent rows against a sub-scoped
  // appointment — entries mismatched the saved scope.) Filtering happens
  // client-side in `allScopedTemplates` instead.
  const templatesQuery = useQuery(
    ["eserv-business-checklists", "appointment-modal-scope"],
    async () => {
      const res = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business/checklist`
      );
      const raw = Array.isArray(res.data) ? res.data : [];
      return raw.map((row: TChecklistTemplateRow) => ({
        ...row,
        required: row.required === true ? true : row.required === false ? false : null,
      })) as TChecklistTemplateRow[];
    },
    { enabled: actualTypeOrNull != null, refetchOnWindowFocus: false }
  );

  /** All templates matching the current FE scope (used for FE scope-divergence rebuilds). */
  const allScopedTemplates = useMemo(() => {
    if (actualTypeOrNull == null) return [];
    const data = templatesQuery.data ?? [];
    return data.filter((r) => templateMatchesAppointmentScope(r, actualTypeOrNull, defaultNum));
  }, [actualTypeOrNull, defaultNum, templatesQuery.data]);

  /** Templates not yet on the appointment — drives the "no autofill" hint UI. */
  const scopedTemplatesNotYetAdded = useMemo(() => {
    const blocked = new Set<number>();
    for (const r of rows) {
      if (!r.dismissed) blocked.add(r.template_id);
    }
    return allScopedTemplates.filter((r) => r.id != null && !blocked.has(r.id));
  }, [allScopedTemplates, rows]);

  // -----------------------------------------------------------------
  // Deterministic rebuild (Phase 2a logic, lifted here in 2b)
  // -----------------------------------------------------------------

  useEffect(() => {
    const untitled = t(`${tBase}.Checklist untitled`);

    // -------- New appointment (no V2 fetch possible yet) --------
    // The V2 endpoint requires an appointment id, so for create-mode the
    // section pulls scoped templates straight from `templatesQuery`. They
    // are POSTed as new entries inside `flushPending` after the BE returns
    // the new appointment id from `saveAppt`'s create response.
    if (appointmentIdNum == null) {
      if (templatesQuery.isLoading) return;

      const next: ChecklistRow[] = [];
      if (allowChecklistAutoOffer && actualTypeOrNull != null) {
        let i = 0;
        for (const tmpl of allScopedTemplates) {
          next.push({
            key: `pending-${actualTypeOrNull}-${defaultNum ?? "p"}-${tmpl.id}-${i++}`,
            template_id: tmpl.id,
            entry_id: null,
            originalLabel: tmpl.label ?? "",
            originalRequired: tmpl.required ?? null,
            label: tmpl.label?.trim() || untitled,
            required: tmpl.required ?? null,
            is_done: false,
            dismissed: false,
          });
        }
      }
      setRows(next);
      return;
    }

    // -------- Existing appointment --------
    if (!checklistQuery.isSuccess) return;

    // savedScope is the appointment's BE scope (passed from the parent). The
    // FE diverges when the user picks a different (type, default) without
    // saving. Treat them as the "saved" scope when they match.
    const isAtSavedScope =
      savedScope != null &&
      savedScope.type === actualTypeOrNull &&
      savedScope.default === defaultNum;

    if (!isAtSavedScope && templatesQuery.isLoading) return;

    const next: ChecklistRow[] = [];

    if (isAtSavedScope) {
      for (const e of checklistQuery.data ?? []) {
        const isPlaceholder = e.entry_id == null;
        // Q3=B: when the user opted out via "(No auto-fill)", drop placeholders.
        if (isPlaceholder && !allowChecklistAutoOffer) continue;
        next.push({
          key: isPlaceholder ? `tmpl-${e.id}` : `entry-${e.entry_id}`,
          template_id: e.id,
          entry_id: e.entry_id ?? null,
          originalLabel: e.label ?? "",
          originalRequired: e.required ?? null,
          label: e.label?.trim() || untitled,
          required: e.required ?? null,
          is_done: Boolean(e.is_done),
          dismissed: false,
        });
      }
    } else {
      for (const e of checklistQuery.data ?? []) {
        if (e.entry_id == null) continue;
        next.push({
          key: `entry-${e.entry_id}`,
          template_id: e.id,
          entry_id: e.entry_id,
          originalLabel: e.label ?? "",
          originalRequired: e.required ?? null,
          label: e.label ?? "",
          required: e.required ?? null,
          is_done: Boolean(e.is_done),
          dismissed: true,
        });
      }
      if (allowChecklistAutoOffer && actualTypeOrNull != null) {
        let i = 0;
        for (const tmpl of allScopedTemplates) {
          next.push({
            key: `pending-${actualTypeOrNull}-${defaultNum ?? "p"}-${tmpl.id}-${i++}`,
            template_id: tmpl.id,
            entry_id: null,
            originalLabel: tmpl.label ?? "",
            originalRequired: tmpl.required ?? null,
            label: tmpl.label?.trim() || untitled,
            required: tmpl.required ?? null,
            is_done: false,
            dismissed: false,
          });
        }
      }
    }

    setRows(next);
  }, [
    appointmentIdNum,
    checklistQuery.isSuccess,
    checklistQuery.data,
    templatesQuery.isLoading,
    allScopedTemplates,
    actualTypeOrNull,
    defaultNum,
    allowChecklistAutoOffer,
    savedScope,
    t,
  ]);

  // Reset when the modal moves to a different appointment.
  useEffect(() => {
    setRows([]);
  }, [appointmentIdNum]);

  const displayRows = useMemo(() => rows.filter((r) => !r.dismissed), [rows]);

  // -----------------------------------------------------------------
  // Save flow — diff `rows` vs the BE existing entries.
  // -----------------------------------------------------------------

  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const checklistDataRef = useRef<IChecklistWithResponses[] | undefined>(checklistQuery.data);
  checklistDataRef.current = checklistQuery.data;

  const flushPending = async (apptId: number) => {
    const data = checklistDataRef.current ?? [];
    const currentRows = rowsRef.current;

    const existingEntries = data.filter(
      (e): e is IChecklistWithResponses & { entry_id: number } => e.entry_id != null
    );
    const rowsByEntryId = new Map<number, ChecklistRow>();
    for (const r of currentRows) {
      if (r.entry_id != null) rowsByEntryId.set(r.entry_id, r);
    }

    let dispatched = false;

    // 1) Deletes: existing entries absent from rows OR present-but-dismissed.
    for (const e of existingEntries) {
      const r = rowsByEntryId.get(e.entry_id);
      if (!r || r.dismissed) {
        await deleteAppointmentChecklistEntry(apptId, e.entry_id);
        dispatched = true;
      }
    }

    // 2) Patches: kept rows whose label/required differ from the original.
    for (const r of currentRows) {
      if (r.entry_id == null) continue;
      if (r.dismissed) continue;
      if (r.label === r.originalLabel && r.required === r.originalRequired) continue;
      await patchAppointmentChecklistEntry(apptId, r.entry_id, {
        label: r.label,
        required: r.required,
      });
      dispatched = true;
    }

    // 3) Posts: rows with no entry_id and not dismissed (Q1=A: visible saves).
    for (const r of currentRows) {
      if (r.entry_id != null) continue;
      if (r.dismissed) continue;
      await postAppointmentChecklistEntry(apptId, r.template_id);
      dispatched = true;
    }

    if (dispatched) {
      // Invalidate so V2 refetches; the build effect re-runs on data change
      // (rebuild gate is data-driven via deps, no manual ref to reset).
      await queryClient.invalidateQueries(["appointment-checklists", apptId]);
    }
  };

  // -----------------------------------------------------------------
  // Row mutations
  // -----------------------------------------------------------------

  const updateRowByKey = (key: string, patch: Partial<ChecklistRow>) => {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  };

  const handleLabelChange = (row: ChecklistRow, nextLabel: string) => {
    updateRowByKey(row.key, { label: nextLabel });
  };

  const handleRequiredChange = (row: ChecklistRow, checked: boolean) => {
    updateRowByKey(row.key, { required: checked });
  };

  const handleDismissRow = (row: ChecklistRow) => {
    if (row.entry_id == null) {
      // Pending POST — drop the row entirely so it doesn't reappear.
      setRows((prev) => prev.filter((r) => r.key !== row.key));
      return;
    }
    updateRowByKey(row.key, { dismissed: true });
  };

  const handleUndoPatchesForRow = (row: ChecklistRow) => {
    updateRowByKey(row.key, {
      label: row.originalLabel,
      required: row.originalRequired,
    });
  };

  /** Picker confirm — append, dedupe by template_id (audit 005 §F7). */
  const handleConfirmTemplatesFromPicker = (picked: TChecklistPickerRow[]) => {
    if (!picked.length) return;
    const untitled = t(`${tBase}.Checklist untitled`);
    setRows((prev) => {
      const blockedTemplateIds = new Set(
        prev.filter((r) => !r.dismissed).map((r) => r.template_id)
      );
      const next = [...prev];
      let i = next.length;
      for (const tmpl of picked) {
        if (blockedTemplateIds.has(tmpl.id)) continue;
        const label = tmpl.label?.trim() || untitled;
        next.push({
          key: `picker-${Date.now()}-${i++}`,
          template_id: tmpl.id,
          entry_id: null,
          originalLabel: tmpl.label ?? "",
          originalRequired:
            tmpl.required === true ? true : tmpl.required === false ? false : null,
          label,
          required:
            tmpl.required === true ? true : tmpl.required === false ? false : null,
          is_done: false,
          dismissed: false,
        });
      }
      return next;
    });
  };

  return {
    rows,
    displayRows,
    scopedTemplatesNotYetAdded,
    isLoading: checklistQuery.isLoading,
    isError: checklistQuery.isError,
    templatesIsLoading: templatesQuery.isLoading,
    handleLabelChange,
    handleRequiredChange,
    handleDismissRow,
    handleUndoPatchesForRow,
    handleConfirmTemplatesFromPicker,
    flushPending,
  };
}
